'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  ClipboardList,
  BarChart3,
  Database,
  ChevronLeft,
  ChevronRight,
  Settings,
  Users,
  Ship,
  Bell,
  HelpCircle,
  LogOut,
  FileSpreadsheet,
  FileText,
  AlertCircle,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const NAV_GROUPS = [
  {
    label: 'Core',
    items: [
      { href: '/inspection-application', icon: ClipboardList, label: 'Inspection App', badge: null },
      { href: '/inspection-data-board', icon: Database, label: 'Data Board', badge: '24' },
      { href: '/analytics-dashboard', icon: BarChart3, label: 'Analytics', badge: null },
    ],
  },
  {
    label: 'Workflow',
    items: [
      { href: '/excel-import', icon: FileSpreadsheet, label: 'Excel Import', badge: null },
      { href: '/follow-up-reports', icon: FileText, label: 'Follow-Up Reports', badge: null },
      { href: '/open-inspections', icon: AlertCircle, label: 'Open Inspections', badge: null },
    ],
  },
  {
    label: 'Fleet',
    items: [
      { href: '#', icon: Ship, label: 'Vessels', badge: null },
      { href: '#', icon: Users, label: 'Inspectors', badge: null },
    ],
  },
  {
    label: 'System',
    items: [
      { href: '#', icon: Bell, label: 'Notifications', badge: '3' },
      { href: '#', icon: Settings, label: 'Settings', badge: null },
      { href: '#', icon: HelpCircle, label: 'Help', badge: null },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  return (
    <aside
      className="fixed left-0 top-0 h-full z-40 flex flex-col border-r bg-white transition-all duration-300 ease-in-out"
      style={{
        width: collapsed ? '64px' : '240px',
        borderColor: 'hsl(var(--border))',
      }}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-3 border-b flex-shrink-0" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="flex items-center gap-2 overflow-hidden">
          <AppLogo size={32} />
          {!collapsed && (
            <div className="flex flex-col leading-tight overflow-hidden">
              <span className="font-bold text-sm text-primary truncate">VesselAudit</span>
              <span className="text-xs text-muted truncate">Anglo American</span>
            </div>
          )}
        </div>
      </div>

      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
        {NAV_GROUPS.map((group) => (
          <div key={`group-${group.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 mb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--muted-foreground))' }}>
                {group.label}
              </p>
            )}
            <div className="px-2 space-y-0.5">
              {group.items.map((item) => {
                const isActive = pathname === item.href;
                const Icon = item.icon;
                return (
                  <Link
                    key={`nav-${item.href}-${item.label}`}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`sidebar-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold"
                            style={{ background: 'hsl(218 70% 22% / 0.1)', color: 'hsl(218 70% 22%)' }}>
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User + Collapse */}
      <div className="border-t p-2 flex-shrink-0" style={{ borderColor: 'hsl(var(--border))' }}>
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 rounded-lg mb-1 hover:bg-muted cursor-pointer transition-colors">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: 'hsl(var(--primary))' }}>
              RK
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-xs font-semibold truncate">Ravi Kumar</p>
              <p className="text-xs truncate" style={{ color: 'hsl(var(--muted-foreground))' }}>Fleet Manager</p>
            </div>
            <LogOut size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center h-8 rounded-lg transition-colors hover:bg-muted"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
}